import {
  MAX_ATTACHMENT_BYTES,
  VaultError,
  isAttachment,
  isEncryptedAttachment,
} from "./model";
import { folderOf } from "./paths";

const mimeByExtension: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
};
const extensionByMime: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp",
};

const isImageFile = (path: string): boolean =>
  isAttachment(path) || isEncryptedAttachment(path);

export function attachmentMime(path: string): string | null {
  if (!isImageFile(path)) return null;
  return mimeByExtension[path.replace(/\.age$/, "").split(".").at(-1)!.toLowerCase()] ?? null;
}

export function imageExtension(file: Pick<File, "type" | "name">): string | null {
  const mime = file.type.toLowerCase();
  if (mime) return extensionByMime[mime] ?? null;
  const extension = file.name.split(".").at(-1)?.toLowerCase() ?? "";
  return extension in mimeByExtension ? extension : null;
}

/** A new image path: the shared attachments/ folder, or an encrypted folder's own. */
export function newAttachmentPath(
  file: Pick<File, "type" | "name">,
  encryptedFolder: string | null = null,
): string {
  const extension = imageExtension(file);
  if (!extension) throw new VaultError("invalid_request", "Use a PNG, JPEG, GIF, or WebP image.");
  const name = `${crypto.randomUUID()}.${extension}`;
  return encryptedFolder ? `${encryptedFolder}/attachments/${name}.age` : `attachments/${name}`;
}

export async function encodeAttachment(file: File): Promise<string> {
  if (!imageExtension(file))
    throw new VaultError("invalid_request", "Use a PNG, JPEG, GIF, or WebP image.");
  if (!file.size || file.size > MAX_ATTACHMENT_BYTES)
    throw new VaultError("vault_too_large", "Images must be smaller than 5 MiB.");
  const data = new Uint8Array(await file.arrayBuffer());
  if (data.length !== file.size) throw new VaultError("invalid_request", "Image read was incomplete.");
  let binary = "";
  for (let index = 0; index < data.length; index += 32768) {
    binary += String.fromCharCode(...data.subarray(index, index + 32768));
  }
  return btoa(binary);
}

export function attachmentReference(notePath: string, assetPath: string): string {
  if (!isImageFile(assetPath)) throw new VaultError("invalid_vault", "Invalid image path.");
  const from = folderOf(notePath).split("/").filter(Boolean);
  const to = assetPath.split("/");
  // Shared images count up to the root, as they always have; an encrypted folder's
  // images are linked from the nearest common folder, so they read the same from the CLI.
  let shared = 0;
  if (isEncryptedAttachment(assetPath))
    while (shared < from.length && shared < to.length - 1 && from[shared] === to[shared]) shared++;
  return `${"../".repeat(from.length - shared)}${to.slice(shared).join("/")}`;
}

export function resolveAttachmentPath(notePath: string, reference: string): string | null {
  if (!reference || /[?#]/.test(reference) || /^[a-z][\w+.-]*:/i.test(reference) || reference.startsWith("/"))
    return null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(reference);
  } catch {
    return null;
  }
  const parts = folderOf(notePath).split("/").filter(Boolean);
  for (const part of decoded.split("/")) {
    if (part === "." || !part) continue;
    if (part === "..") {
      if (!parts.length) return null;
      parts.pop();
    } else {
      parts.push(part);
    }
  }
  const path = parts.join("/");
  return isImageFile(path) ? path : null;
}
